import { useEffect, useState } from 'react'
import {
    ConsolidatedReportScheduleService,
    ErrorConstant,
    PaginationType,
} from 'utility'

export const GetConsolidatedReportScheduleList = (
    paginationType = PaginationType.Default,
) => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 25,
        search: '',
        status: 'all',
        all: paginationType,
    })

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const query = filter.all === PaginationType.All
                    ? {}
                    : {
                          pageNumber: filter.PageNumber,
                          pageSize: filter.PageSize,
                          ...(filter.search ? { search: filter.search } : {}),
                          ...(filter.status && filter.status !== 'all'
                              ? { status: filter.status }
                              : {}),
                      }
                const result = await ConsolidatedReportScheduleService.list(query)
                setData(result?.Items || [])
                setTotalCount(result?.TotalCount || 0)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [filter, paginationType])

    const pageChanged = page => {
        setFilter(prev => ({ ...prev, PageNumber: page }))
    }

    const filterChanged = changes => {
        setFilter(prev => ({ ...prev, PageNumber: 1, ...changes }))
    }

    const refresh = () => {
        setFilter(prev => ({ ...prev }))
    }

    return {
        data,
        setData,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        refresh,
        filterChanged,
    }
}

export const GetConsolidatedReportRuns = (options = {}) => {
    const { scheduleId, pageSize = 25, enabled = true } = options
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: pageSize,
        scheduleId,
        status: 'all',
    })

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            scheduleId,
            PageNumber: 1,
        }))
    }, [scheduleId])

    useEffect(() => {
        if (!enabled) {
            return
        }
        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const query = {
                    pageNumber: filter.PageNumber,
                    pageSize: filter.PageSize,
                    ...(filter.scheduleId
                        ? { scheduleId: filter.scheduleId }
                        : {}),
                    ...(filter.status && filter.status !== 'all'
                        ? { status: filter.status }
                        : {}),
                }
                const result =
                    await ConsolidatedReportScheduleService.listRuns(query)
                setData(result?.Items || [])
                setTotalCount(result?.TotalCount || 0)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [filter, enabled])

    const pageChanged = page => {
        setFilter(prev => ({ ...prev, PageNumber: page }))
    }

    const filterChanged = changes => {
        setFilter(prev => ({ ...prev, PageNumber: 1, ...changes }))
    }

    const refresh = () => {
        setFilter(prev => ({ ...prev }))
    }

    return {
        data,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        refresh,
        filterChanged,
    }
}

export const useConsolidatedReportSchedule = scheduleId => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchSchedule = async id => {
        if (!id) return
        try {
            setLoading(true)
            setError('')
            const result = await ConsolidatedReportScheduleService.get(id)
            const schedule = result?.Schedule || result
            let columns = schedule?.Columns || result?.Columns
            if (!Array.isArray(columns) || columns.length === 0) {
                try {
                    const columnResult =
                        await ConsolidatedReportScheduleService.getColumns(id)
                    columns = columnResult?.Columns || columnResult
                } catch {
                    columns = schedule?.Columns
                }
            }
            setData({
                ...schedule,
                ...(Array.isArray(columns) && columns.length
                    ? { Columns: columns }
                    : {}),
            })
        } catch (err) {
            setError(err)
            setData(null)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (scheduleId) fetchSchedule(scheduleId)
    }, [scheduleId])

    return { data, loading, error, refetch: () => fetchSchedule(scheduleId) }
}
